stimulusApp.register(
  "search--idea-desktop",
  class extends Stimulus.Controller {
    static targets = ["locations"]
    static values = {
      ideaCount: Number,
    }

    #hideTimeout = null
    #moveHandler = null
    #activePopout = null
    #lastFiredIdeaId = null
    #ideaSessionId = null
    #activeIdeaId = null
    #activeTriangle = null
    #triangleTimeout = null
    #activeCard = null

    disconnect() {
      this.#cancelIntent()
      this.#activePopout = null
      this.#lastFiredIdeaId = null
      this.#activeIdeaId = null
      this.#ideaSessionId = null
      this.#activeCard?.classList.remove("search-ideas--i_active")
      this.#activeCard = null
    }

    show(event) {
      const { currentTarget } = event
      const id = String(event.params.ideaId)
      // Mid-flight to the popout: while the cursor is inside the active safe triangle,
      // grazing another card must not cancel the intent or switch the popout. The move
      // handler switches once the cursor genuinely leaves the triangle over a card.
      if (
        this.#activeTriangle &&
        id !== this.#activeIdeaId &&
        this.#pointInTriangle(
          { x: event.clientX, y: event.clientY },
          this.#activeTriangle,
        )
      ) {
        return
      }
      this.#switchTo(currentTarget, id)
    }

    #switchTo(card, id) {
      this.#cancelIntent()
      this.#activePopout = null
      this.locationsTargets.forEach((popout) => {
        if (popout.dataset.ideaId === id) {
          this.#activatePopout(popout)
          this.#alignPopoutToCard(popout, card)
        } else {
          this.#deactivatePopout(popout)
        }
      })
      this.#activeCard?.classList.remove("search-ideas--i_active")
      card?.classList.add("search-ideas--i_active")
      this.#activeCard = card
      this.#activeIdeaId = id
      if (id !== this.#lastFiredIdeaId) {
        this.#lastFiredIdeaId = id
        this.#ideaSessionId = HB.uuidv4()
        const siblings = card?.parentNode
          ? Array.from(card.parentNode.children)
          : []
        const position = siblings.indexOf(card)
        Activity.capture("search-ideas idea opened", {
          idea_id: id,
          position,
          surface: "desktop-popout",
          trigger: "hover",
          idea_session_id: this.#ideaSessionId,
        })
      }
    }

    // Exposed for the location-form controller to attribute clicks back to the
    // currently-open idea on desktop (via the search--idea-desktop outlet).
    currentIdeaSession() {
      if (!this.#activeIdeaId) return null
      return { ideaId: this.#activeIdeaId, sessionId: this.#ideaSessionId }
    }

    onLocationsLoaded() {
      if (this.#activeCard && this.#activeIdeaId) {
        this.#switchTo(this.#activeCard, this.#activeIdeaId)
      }
    }

    #alignPopoutToCard(popout, card) {
      const anchor = popout.offsetParent
      if (!anchor) return
      const cardRect = card.getBoundingClientRect()
      const anchorRect = anchor.getBoundingClientRect()
      const paddingTop = parseFloat(getComputedStyle(popout).paddingTop) || 0
      const margin = 16

      // Align the popout's first card with the hovered card, then clamp into the
      // viewport: shift up if it would overflow the bottom, but never above the
      // destinations panel (its offsetParent) so an over-tall popout stays
      // attached to the panel instead of floating up to the viewport edge. Stop
      // at the panel's rounded top corner so the popout's straight edge lines up
      // with the panel's. When the list is taller than the available space this
      // top limit wins, so the head of the list stays visible.
      const anchorStyle = getComputedStyle(anchor)
      const cornerRadius = Math.max(
        parseFloat(anchorStyle.borderTopLeftRadius) || 0,
        parseFloat(anchorStyle.borderTopRightRadius) || 0,
      )
      const topLimit = Math.max(margin, anchorRect.top + cornerRadius)
      const naturalTop = cardRect.top - paddingTop
      const maxTop = window.innerHeight - margin - popout.offsetHeight
      const viewportTop = Math.max(topLimit, Math.min(naturalTop, maxTop))

      popout.style.top = `${viewportTop - anchorRect.top}px`
    }

    keep() {
      this.#cancelIntent()
    }

    reset() {
      this.#hideAll()
    }

    hideFromCard(event) {
      if (!this.#activePopout) return
      this.#trackHoverIntent(event)
    }

    hideFromPopout() {
      this.#hideTimeout = setTimeout(() => this.#hideAll(), 150)
    }

    #trackHoverIntent(event) {
      // Tear down any prior intent first: `show` no longer cancels on every card
      // enter (it keeps the popout while inside the triangle), so without this each
      // re-entry would stack another document mousemove listener with a stale triangle.
      this.#cancelIntent()

      const rect = this.#settledRect(this.#activePopout)
      if (!rect || !rect.width || !rect.height) {
        this.#hideAll()
        return
      }

      const exit = { x: event.clientX, y: event.clientY }
      // Protection zone = triangle ∪ popout rect ∪ the idea-cards list. Triangle bridges any gap from
      // exit to the popout's card-facing corners (right edge in RTL where the popout sits on the leading
      // side, left edge otherwise); the rect covers diagonal paths into popout territory. While the cursor
      // is inside the triangle the current popout is held even when it grazes a sibling card; the popout
      // only switches once the cursor leaves the triangle over a different card (or hides if it leaves the
      // list entirely).
      const cardFacingX = HB.isRTL() ? rect.right : rect.left
      const triangle = [
        exit,
        { x: cardFacingX, y: rect.top },
        { x: cardFacingX, y: rect.bottom },
      ]
      const list = event.currentTarget.closest(".search-ideas--list")
      const listRect = list?.getBoundingClientRect()

      this.#activeTriangle = triangle
      // The triangle is a transition grace period, not a permanent hold — expire it
      // so parking the cursor in its dead zone can't keep the popout open forever.
      this.#triangleTimeout = setTimeout(() => {
        this.#activeTriangle = null
        this.#triangleTimeout = null
      }, 500)

      this.#moveHandler = (e) => {
        const p = { x: e.clientX, y: e.clientY }
        // Inside the still-active triangle: protected path toward the popout, keep the current one.
        if (
          this.#activeTriangle &&
          this.#pointInTriangle(p, this.#activeTriangle)
        )
          return
        // Left the triangle over a different card: switch to it (the card's own
        // `mouseenter` may not re-fire if the cursor was already inside it).
        const card = document
          .elementFromPoint(p.x, p.y)
          ?.closest(".search-ideas--i")
        if (card) {
          const id = card.getAttribute(
            "data-search--idea-desktop-idea-id-param",
          )
          if (id && id !== this.#activeIdeaId) this.#switchTo(card, id)
          return
        }
        if (
          this.#pointInRect(p, rect) ||
          (listRect && this.#pointInRect(p, listRect))
        ) {
          return
        }
        this.#hideAll()
      }
      document.addEventListener("mousemove", this.#moveHandler)
    }

    #pointInRect(p, rect) {
      return (
        p.x >= rect.left &&
        p.x <= rect.right &&
        p.y >= rect.top &&
        p.y <= rect.bottom
      )
    }

    // Transform-independent so the triangle uses the popout's settled position, not its mid-animation one.
    #settledRect(popout) {
      const anchor = popout.offsetParent
      if (!anchor) return null
      const anchorRect = anchor.getBoundingClientRect()
      const anchorStyle = getComputedStyle(anchor)
      const borderLeft = parseFloat(anchorStyle.borderLeftWidth) || 0
      const borderTop = parseFloat(anchorStyle.borderTopWidth) || 0
      const left = anchorRect.left + borderLeft + popout.offsetLeft
      const top = anchorRect.top + borderTop + popout.offsetTop
      return {
        left,
        top,
        right: left + popout.offsetWidth,
        bottom: top + popout.offsetHeight,
        width: popout.offsetWidth,
        height: popout.offsetHeight,
      }
    }

    #pointInTriangle(p, [a, b, c]) {
      const sign = (p1, p2, p3) =>
        (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y)
      const d1 = sign(p, a, b)
      const d2 = sign(p, b, c)
      const d3 = sign(p, c, a)
      const hasNeg = d1 < 0 || d2 < 0 || d3 < 0
      const hasPos = d1 > 0 || d2 > 0 || d3 > 0
      return !(hasNeg && hasPos)
    }

    #cancelIntent() {
      if (this.#hideTimeout) {
        clearTimeout(this.#hideTimeout)
        this.#hideTimeout = null
      }
      if (this.#moveHandler) {
        document.removeEventListener("mousemove", this.#moveHandler)
        this.#moveHandler = null
      }
      if (this.#triangleTimeout) {
        clearTimeout(this.#triangleTimeout)
        this.#triangleTimeout = null
      }
      this.#activeTriangle = null
    }

    #hideAll() {
      this.#cancelIntent()
      this.locationsTargets.forEach((popout) => this.#deactivatePopout(popout))
      this.#activePopout = null
      this.#lastFiredIdeaId = null
      this.#activeIdeaId = null
      this.#ideaSessionId = null
      this.#activeCard?.classList.remove("search-ideas--i_active")
      this.#activeCard = null
    }

    #activatePopout(popout) {
      popout.classList.remove("hidden")
      this.#activePopout = popout
    }

    #deactivatePopout(popout) {
      popout.classList.add("hidden")
    }
  },
)
